// 商品詳細の畳数切替（sections/aicon-product-buybox.liquid の <aicon-room-size-switch>）。
//
// 「アマゾンのように、一旦、畳数だけで商品詳細ページで選べるようにしたい」
// （日高さん指示 2026-09-07）。
//
// ★ 畳数はバリエーションではない ★
//   このストアは 1型番＝1商品で、商品オプションは「購入方法」だけ。
//   畳数を変える＝**別の商品ページへ移動する**ので、ここが作るのは
//   セレクトではなく **<a> のボタン列**である。
//   ＝ 右クリックで別タブ、長押しでプレビュー、戻るで戻れる。素のリンクの利点をそのまま使う。
//
// ★ 選択肢は backend から引く ★
//   GET {apiBase}/api/compare/room-sizes?model=<型番>
//     → { success, data: { options: [{ tatami, label, modelNumber, handle, price, isCurrent }],
//                          currentTatami, series, reason } }
//   同じシリーズの畳数ちがいは Shopify 側だけでは辿れない（シリーズの metafield が無い）。
//   選定ルールは backend/src/services/business/model-room-sizes.service.ts。
//
// ★ 価格は Shopify から取り直す ★
//   API の price は DB 値で、並べ替えと目安のためのもの。ボタンに出す金額は
//   /products/{handle}.js から取る。DB と Shopify で食い違った額を出さないため
//   （assets/aicon-compare-alt.js と同じ約束）。
//   ついでにこれが**実在確認**にもなる。Shopify へ未公開の handle は 404 になるので、
//   押しても商品が無いボタンを作らずに済む。
//
// ★ 電圧（100V/200V）も同じ要素が描く（日高さん指示 2026-09-12）★
//   「100Vと200V両方ある機種、畳数同様に詳細ページから選択できるように」。
//   同じ商品ページのための情報なので **同じ応答に相乗り**させてある
//   （data.voltageOptions）。別エンドポイントにすると同じ商品を2回引くことになる。
//   畳数と同じく「別の商品ページへのリンク列」で、**両方ある機種だけ**に出る。
//
// ★ 出せないときは何もしない ★
//   要素は hidden で描かれており、**畳数か電圧のどちらかが**2つ以上そろったときだけ外す。
//   API が落ちている・シリーズ未登録・展開が1つだけ、いずれも
//   「切替が無い、いつもの商品ページ」になるだけで、見出しだけが残ることはない。

/** 本体価格として扱うバリエーション。requires_shipping = true が本体（工事なし）。 */
const bodyPrice = (product) => {
  const body = (product?.variants ?? []).find((v) => v.requires_shipping);
  const price = body?.price ?? product?.price;
  return typeof price === 'number' ? price : null;
};

/** Shopify の価格（最小単位＝銭）を「¥138,502」にする。JPY は付けない（一覧と同じ扱い）。 */
const yen = (minorUnits) => `¥${Math.round(minorUnits / 100).toLocaleString('ja-JP')}`;

/**
 * innerHTML に差し込む文字列を無害化する。
 * 畳数と金額は数値から作るので元々安全だが、見出しはセクション設定＝人が編集する文字列。
 * テーマエディタしか触れないとはいえ、HTML として解釈させる理由が無い。
 */
const esc = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/**
 * 診断の見積ID（?aiq=）を、畳数・電圧を切り替えた先にも引き継ぐ（2026-10-17）。
 *
 * 診断 → 比較ページ → 商品ページと来たお客様が、ここで畳数を変えると ID が落ち、
 * 写真見積と診断のつながりが切れる（#1158 は 6畳用 → 12畳用と切り替えていた）。
 * 形は backend の services/quote/diagnosisQuoteLink.ts と揃える。
 */
const DIAGNOSIS_QUERY_KEY = 'aiq';
const DIAGNOSIS_ID_PATTERN = /^c[a-z0-9]{20,32}$/;
const diagnosisSuffix = () => {
  try {
    const id = new URLSearchParams(window.location.search).get(DIAGNOSIS_QUERY_KEY) || '';
    return DIAGNOSIS_ID_PATTERN.test(id) ? `?${DIAGNOSIS_QUERY_KEY}=${encodeURIComponent(id)}` : '';
  } catch {
    return '';
  }
};

class AiconRoomSizeSwitch extends HTMLElement {
  async connectedCallback() {
    // 二重実行の防止。custom element は移動で再接続されることがある
    if (this.dataset.aiconReady) return;
    this.dataset.aiconReady = '1';

    const apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
    const model = this.dataset.model || '';
    if (!apiBase || !model) return;

    let sizes = [];
    let volts = [];
    try {
      const res = await fetch(
        `${apiBase}/api/compare/room-sizes?model=${encodeURIComponent(model)}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok) return;
      const json = await res.json();
      if (!json?.success) return;
      sizes = Array.isArray(json.data?.options) ? json.data.options : [];
      volts = Array.isArray(json.data?.voltageOptions) ? json.data.voltageOptions : [];
    } catch {
      // 通信できない／CORS で弾かれた。切替を出さないだけで商品ページは成立する
      return;
    }

    if (sizes.length < 2 && volts.length < 2) return;

    /*
     * Shopify 側の実在と価格を確認する。
     *
     * ★ handle 単位で1回だけ引く（2026-09-12）★
     *   畳数の一覧と電圧の一覧には**同じ handle（＝いま開いている商品）が必ず入る**。
     *   素直に両方引くと同じ JSON を2回取りに行く。Map に溜めて共有する。
     *
     * ★ 商品JSON 1本ぶんを選択肢の数だけ引くので軽くはない（2026-09-07 レビュー指摘）★
     *   目的は「実在確認」と「Shopify 側の価格」の2つだけ。
     *   重くなったら、backend 側で publishedAt と突合済みの価格まで返す形にして
     *   ここの fetch を落とすこと。いまは1商品ページで最大12本・静的JSONなので許容する。
     */
    const priceCache = new Map();
    const priceOf = (handle) => {
      if (!priceCache.has(handle)) {
        priceCache.set(
          handle,
          (async () => {
            try {
              const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
                headers: { Accept: 'application/json' },
              });
              if (!res.ok) return null; // 未公開・存在しない handle は選択肢にしない
              return bodyPrice(await res.json());
            } catch {
              return null;
            }
          })()
        );
      }
      return priceCache.get(handle);
    };

    /** Shopify に実在し、本体価格が読めた選択肢だけを残す。 */
    const usable = async (options) => {
      const checked = await Promise.all(
        options.map(async (o) => {
          if (!o?.handle || !o?.label) return null;
          const price = await priceOf(o.handle);
          return price === null ? null : { ...o, price };
        })
      );
      // 自分1つしか残らなかったら切り替える先が無い
      const kept = checked.filter(Boolean);
      return kept.length >= 2 ? kept : [];
    };

    const [okSizes, okVolts] = await Promise.all([
      sizes.length >= 2 ? usable(sizes) : [],
      volts.length >= 2 ? usable(volts) : [],
    ]);

    if (okSizes.length === 0 && okVolts.length === 0) return;

    this.render(okSizes, okVolts);
    this.hidden = false;
  }

  /**
   * 切替のボタン列を描く。畳数が先、電圧が後。
   *
   * ★ いま開いているものだけ <span> にする ★
   *   自分自身へのリンクは押しても何も起きないので、リンクにしない。
   *   aria-current で「いまここ」を支援技術にも伝える。
   *   判定は handle 同士（型番の表記ゆれを挟まない）。
   *
   * ★ 電圧は畳数と同じ見た目にする ★
   *   同じ .aicon-sizes__* を使う。並ぶ2列が別物に見えると、
   *   片方だけ「選べるもの」と読まれてしまう。
   */
  render(sizes, volts) {
    const currentHandle = this.dataset.currentHandle || '';
    const suffix = diagnosisSuffix();

    const group = (label, options) => {
      if (!options || options.length === 0) return '';
      const current =
        options.find((o) => o.handle === currentHandle) ?? options.find((o) => o.isCurrent);
      const items = options
        .map((o) => {
          const isCurrent = o.handle === currentHandle || (!currentHandle && o.isCurrent);
          const inner = `<span class="aicon-sizes__size">${esc(o.label)}</span><span class="aicon-sizes__price">${yen(o.price)}</span>`;
          return isCurrent
            ? `<span class="aicon-sizes__item is-current" aria-current="true">${inner}</span>`
            : `<a class="aicon-sizes__item" href="/products/${encodeURIComponent(o.handle)}${suffix}">${inner}</a>`;
        })
        .join('');
      return `
        <div class="aicon-sizes__group">
          <p class="aicon-sizes__label">${esc(label)}${current ? `: <b>${esc(current.label)}</b>` : ''}</p>
          <div class="aicon-sizes__list">${items}</div>
        </div>
      `;
    };

    this.innerHTML =
      group(this.dataset.label || 'サイズ（畳数）', sizes) +
      group(this.dataset.voltageLabel || '電源（電圧）', volts);
  }
}

if (!customElements.get('aicon-room-size-switch')) {
  customElements.define('aicon-room-size-switch', AiconRoomSizeSwitch);
}
